import { Controller } from "@hotwired/stimulus"

// Priority+ / “greedy” overflow toolbar:
// - All direct children are overflowable by default.
// - Elements marked `data-overflow-toolbar-pinned` never overflow.
// - As space shrinks, overflowable items are hidden (right-to-left) and mirrored
//   into the existing `…` dropdown menu.
export default class extends Controller {
  connect() {
    this.resizeObserver = new ResizeObserver(() => this.reflow())
    this.resizeObserver.observe(this.element)
    this._onWindowResize = () => this.reflow()
    window.addEventListener("resize", this._onWindowResize, { passive: true })
    this.reflow()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    if (this._onWindowResize) {
      window.removeEventListener("resize", this._onWindowResize)
      this._onWindowResize = null
    }
  }

  reflow() {
    const menu = this._dropdownMenu()
    if (!menu) return

    // Reset to baseline: show everything overflowable and remove generated items.
    this._showAllOverflowables()
    this._removeGeneratedMenuItems(menu)

    const overflowables = this._overflowableChildren()
    if (overflowables.length === 0) return

    const hidden = []

    // Hide overflowables until the toolbar fits.
    // Use scrollWidth > clientWidth as the simplest signal.
    // Add a tiny epsilon to avoid thrashing on fractional pixels.
    while (this.element.scrollWidth > this.element.clientWidth + 1) {
      const next = overflowables
        .filter((el) => !el.hidden)
        .at(-1)
      if (!next) break

      next.hidden = true
      hidden.push(next)
    }

    if (hidden.length === 0) return

    // Insert overflowed actions into the menu, keeping toolbar left-to-right order.
    const itemsInToolbarOrder = hidden.reverse()
    const frag = document.createDocumentFragment()

    for (const el of itemsInToolbarOrder) {
      const li = this._menuItemForElement(el)
      if (!li) continue
      frag.appendChild(li)
    }

    if (frag.childNodes.length > 0) {
      // Divider between overflowed items and the existing menu items.
      const divider = document.createElement("li")
      divider.dataset.overflowGenerated = "true"
      const hr = document.createElement("hr")
      hr.className = "dropdown-divider"
      divider.appendChild(hr)
      frag.appendChild(divider)

      menu.insertBefore(frag, menu.firstChild)
    }
  }

  _overflowableChildren() {
    return Array.from(this.element.children).filter(
      (el) => !this._isPinned(el)
    )
  }

  _showAllOverflowables() {
    for (const el of this._overflowableChildren()) el.hidden = false
  }

  _isPinned(el) {
    return el.hasAttribute("data-overflow-toolbar-pinned")
  }

  _dropdownMenu() {
    // Prefer the pinned wrapper that contains the ellipsis dropdown.
    // (Edit is also pinned but doesn't have a menu.)
    const pinned = Array.from(
      this.element.querySelectorAll("[data-overflow-toolbar-pinned]")
    )
    for (const el of pinned) {
      const menu = el.querySelector(".dropdown-menu")
      if (menu) return menu
    }
    return null
  }

  _removeGeneratedMenuItems(menu) {
    menu.querySelectorAll("[data-overflow-generated]").forEach((n) => n.remove())
  }

  _menuItemForElement(el) {
    // Only handle simple link buttons for now.
    // (This toolbar currently renders only <a> buttons + pinned wrappers.)
    const link = el.tagName.toLowerCase() === "a" ? el : el.querySelector("a")
    if (!link) return null

    const href = link.getAttribute("href")
    if (!href) return null

    const label =
      link.textContent?.trim() ||
      link.getAttribute("aria-label") ||
      link.getAttribute("title") ||
      "Action"

    const li = document.createElement("li")
    li.dataset.overflowGenerated = "true"

    const a = document.createElement("a")
    a.className = "dropdown-item"
    a.href = href

    // Preserve data-* attributes for Turbo/etc.
    for (const [key, value] of Object.entries(link.dataset || {})) {
      // Skip our internal attrs.
      if (key === "overflowToolbarPinned") continue
      a.dataset[key] = value
    }

    const ariaLabel = link.getAttribute("aria-label")
    if (ariaLabel) a.setAttribute("aria-label", ariaLabel)

    const icon = link.querySelector("i")
    if (icon) {
      const cloned = icon.cloneNode(true)
      cloned.classList.add("fa-fw", "me-2", "text-body-secondary")
      a.appendChild(cloned)
    }

    a.appendChild(document.createTextNode(label))
    li.appendChild(a)

    return li
  }
}
